import { Button, type ButtonProps } from "#button";
import type {
  DefaultError,
  MutateOptions,
  UseMutationResult,
} from "@-/com.npmjs.tanstack__react-query";
import { useCallback, useRef } from "react";

type MutationButtonOwnProps<TData, TError, TVariables, TOnMutateResult> = {
  mutation: UseMutationResult<TData, TError, TVariables, TOnMutateResult>;
  onError?: MutateOptions<TData, TError, TVariables, TOnMutateResult>["onError"];
  onSettled?: MutateOptions<TData, TError, TVariables, TOnMutateResult>["onSettled"];
  onSuccess?: MutateOptions<TData, TError, TVariables, TOnMutateResult>["onSuccess"];
};

type MutationButtonProps<TData, TError, TVariables, TOnMutateResult> = Omit<
  ButtonProps,
  "isLoading" | "onPress"
> &
  MutationButtonOwnProps<TData, TError, TVariables, TOnMutateResult> &
  // Resolved at press time (inside the press lock) so the mutation sees the latest values rather
  // than whatever the component last rendered with. Optional only when the mutation takes no
  // variables (`TVariables` is `void`).
  (TVariables extends void ? { variables?: () => TVariables } : { variables: () => TVariables });

export function MutationButton<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
>({
  mutation,
  onError,
  onSettled,
  onSuccess,
  variables,
  ...rest
}: MutationButtonProps<TData, TError, TVariables, TOnMutateResult>) {
  // Manual lock guards against same-button double-fire while a mutation is in flight. Callers
  // express cross-mutation/form-validity gating via `isDisabled` (forwarded through `{...rest}` to
  // `Button`, which both dims and suppresses `onPress` while preserving hover/tooltip interactivity).
  const isLockedRef = useRef(false);
  const onPress = useCallback(() => {
    if (isLockedRef.current) {
      return;
    }
    isLockedRef.current = true;
    const mutateOptions: MutateOptions<TData, TError, TVariables, TOnMutateResult> = {
      onError,
      onSettled: (data, error, vars, onMutateResult, context) => {
        onSettled?.(data, error, vars, onMutateResult, context);
        isLockedRef.current = false;
      },
      onSuccess,
    };
    if (variables) {
      mutation.mutate(variables(), mutateOptions);
      return;
    }
    // `variables` is omitted only when `TVariables` is `void`.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- void mutations accept `undefined` variables
    mutation.mutate(undefined as TVariables, mutateOptions);
  }, [mutation, variables, onError, onSuccess, onSettled]);

  return <Button {...rest} isLoading={mutation.isPending} onPress={onPress} />;
}
